import React, { useState, useMemo } from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  ShoppingBag,
  Receipt,
  Calendar,
  Clock,
  IceCream,
  FileSpreadsheet,
  Download,
  CreditCard,
  Store,
  PieChart as PieIcon,
  BarChart3,
  Award,
  Zap,
  Sparkles,
  Layers,
  ArrowUpRight,
  ArrowDownRight,
  HelpCircle,
  LayoutDashboard,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useSalesReport,
  useTopProducts,
  useTopFlavors,
  useHourlyReport,
  usePreparationTimeReport,
} from '../../hooks/useReports';
import { MenuEngineeringTab } from './MenuEngineeringTab';
import { WaitersTab } from './WaitersTab';
import { TipsTab } from './TipsTab';
import {
  downloadCsvExport,
  downloadExcelReport,
  downloadAccountingExport,
  PaymentMethodStat,
  FulfillmentStat,
  CategoryStat,
} from '../../services/ReportsService';

// ── Helpers ────────────────────────────────────────────────────────────────────

function today() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgo(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function formatCOP(value: number) {
  return '$ ' + Math.round(value).toLocaleString('es-CO');
}

function getPresetDates(type: 'today' | 'yesterday' | '7d' | 'this_month' | 'last_month') {
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  if (type === 'today') return { from: todayStr, to: todayStr };
  if (type === 'yesterday') {
    const y = new Date(now);
    y.setDate(y.getDate() - 1);
    const yStr = y.toISOString().slice(0, 10);
    return { from: yStr, to: yStr };
  }
  if (type === '7d') {
    const d = new Date(now);
    d.setDate(d.getDate() - 6);
    return { from: d.toISOString().slice(0, 10), to: todayStr };
  }
  if (type === 'this_month') {
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: firstDay.toISOString().slice(0, 10), to: todayStr };
  }
  if (type === 'last_month') {
    const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: firstDay.toISOString().slice(0, 10), to: lastDay.toISOString().slice(0, 10) };
  }
  return { from: todayStr, to: todayStr };
}

// Paletas vibrantes para Donut / Pie Charts
const DONUT_COLORS = [
  '#10B981', // Esmeralda
  '#3B82F6', // Azul real
  '#8B5CF6', // Violeta
  '#F59E0B', // Ámbar
  '#EC4899', // Rosa
  '#06B6D4', // Cyan
  '#F97316', // Naranja
  '#64748B', // Pizarra
];

// ── KPI Card Component ─────────────────────────────────────────────────────────

interface KpiProps {
  label: string;
  value: string;
  sublabel?: string;
  icon: React.ReactNode;
  growth?: number;
  growthLabel?: string;
  loading?: boolean;
  accentBg?: string;
  accentText?: string;
}

function KpiCard({
  label,
  value,
  sublabel,
  icon,
  growth,
  growthLabel = 'vs período ant.',
  loading,
  accentBg = 'bg-primary/10',
  accentText = 'text-primary',
}: KpiProps) {
  const isPositive = growth !== undefined && growth > 0;
  const isNeutral = growth !== undefined && growth === 0;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-border bg-surface p-5 shadow-card transition-all hover:shadow-md">
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-text-muted">{label}</p>
          {loading ? (
            <div className="mt-2 h-8 w-36 animate-pulse rounded bg-muted" />
          ) : (
            <p className="mt-1 text-2xl font-black text-text tracking-tight">{value}</p>
          )}

          {sublabel && !loading && (
            <p className="mt-0.5 text-xs text-text-muted">{sublabel}</p>
          )}

          {/* Growth indicator badge */}
          {growth !== undefined && !loading && (
            <div className="mt-2.5 flex items-center gap-1.5 text-xs">
              <span
                className={`inline-flex items-center gap-0.5 font-bold px-2 py-0.5 rounded-full ${
                  isNeutral
                    ? 'bg-muted text-text-muted'
                    : isPositive
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                    : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
                }`}
              >
                {isPositive ? <ArrowUpRight size={13} /> : isNeutral ? null : <ArrowDownRight size={13} />}
                {isPositive ? '+' : ''}{growth}%
              </span>
              <span className="text-[11px] text-text-muted">{growthLabel}</span>
            </div>
          )}
        </div>

        <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${accentBg} ${accentText} shadow-sm`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

// ── Tooltips ───────────────────────────────────────────────────────────────────

function CustomChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-border bg-surface p-3 shadow-xl text-xs space-y-1 z-50">
      <p className="font-bold text-text border-b border-border pb-1 mb-1">{label}</p>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5 font-medium" style={{ color: p.color || p.fill }}>
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color || p.fill }} />
            {p.name}:
          </span>
          <span className="font-bold text-text">
            {p.dataKey === 'totalRevenue' || p.dataKey === 'revenue' || p.dataKey === 'avgTicket'
              ? formatCOP(p.value)
              : p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Main Page Component ────────────────────────────────────────────────────────

export default function Reports() {
  const [from, setFrom] = useState(daysAgo(6));
  const [to, setTo] = useState(today());
  const [activePreset, setActivePreset] = useState<string>('7d');
  const [chartView, setChartView] = useState<'revenue' | 'orders' | 'avgTicket'>('revenue');
  const [hourlyDate, setHourlyDate] = useState(today());
  const [exporting, setExporting] = useState(false);

  const sales = useSalesReport(from, to);
  const topProducts = useTopProducts(from, to, 10);
  const topFlavors = useTopFlavors(from, to, 10);
  const hourly = useHourlyReport(hourlyDate);
  const prepTime = usePreparationTimeReport(from, to);

  function applyPreset(key: 'today' | 'yesterday' | '7d' | 'this_month' | 'last_month') {
    const dates = getPresetDates(key);
    setFrom(dates.from);
    setTo(dates.to);
    setActivePreset(key);
  }

  async function handleExport() {
    setExporting(true);
    try {
      await downloadCsvExport(from, to);
      toast.success('Reporte CSV descargado con éxito');
    } catch {
      toast.error('Error al descargar reporte CSV');
    } finally {
      setExporting(false);
    }
  }

  const [activeTab, setActiveTab] = useState<'sales' | 'menu-engineering' | 'waiters' | 'tips'>('sales');

  async function handleExportAccounting() {
    setExporting(true);
    try {
      await downloadAccountingExport(from, to);
      toast.success('Libro contable fiscal descargado con éxito');
    } catch {
      toast.error('Error al descargar libro contable');
    } finally {
      setExporting(false);
    }
  }

  async function handleExportExcel() {
    setExporting(true);
    try {
      await downloadExcelReport(from, to);
      toast.success('Reporte Excel (.xlsx) generado con éxito');
    } catch {
      toast.error('Error al generar reporte Excel');
    } finally {
      setExporting(false);
    }
  }

  const dailyData = useMemo(() => {
    return (sales.data?.daily ?? []).map(d => ({
      ...d,
      label: d.date.slice(5), // MM-DD
    }));
  }, [sales.data?.daily]);

  const hourlyData = hourly.data?.hours ?? [];
  const paymentMethods: PaymentMethodStat[] = sales.data?.byPaymentMethod ?? [];
  const fulfillmentChannels: FulfillmentStat[] = sales.data?.byFulfillment ?? [];
  const categories: CategoryStat[] = sales.data?.byCategory ?? [];
  const insights = sales.data?.insights;
  const growth = sales.data?.growth;

  return (
    <div className="min-h-screen bg-bg p-4 md:p-8">
      <div className="mx-auto max-w-7xl space-y-6">

        {/* ── 1. Header con botones de exportación ── */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <BarChart3 size={18} />
              </span>
              <h1 className="text-2xl font-black text-text tracking-tight">Reportes y KPIs de Ventas</h1>
            </div>
            <p className="text-sm text-text-muted mt-0.5">
              Métricas clave de facturación, medios de pago, canales y productos más rentables
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleExportAccounting}
              disabled={exporting || sales.isLoading}
              className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 px-3.5 py-2.5 text-xs sm:text-sm font-bold text-white shadow-sm transition active:scale-95 disabled:opacity-50"
              title="Descargar libro contable con ingresos, costos estimados y utilidades para el contador"
            >
              <FileSpreadsheet size={16} />
              Libro Contable (Excel)
            </button>
            <button
              onClick={handleExportExcel}
              disabled={exporting || sales.isLoading}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 px-3.5 py-2.5 text-xs sm:text-sm font-bold text-white shadow-sm transition active:scale-95 disabled:opacity-50"
              title="Descargar libro Excel profesional (.xlsx)"
            >
              <FileSpreadsheet size={16} />
              {exporting ? 'Generando…' : 'Excel (.xlsx)'}
            </button>
            <Link
              to="/kpi"
              className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-600/30 bg-emerald-50 dark:bg-emerald-950/50 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 px-3.5 py-2.5 text-xs sm:text-sm font-bold text-emerald-700 dark:text-emerald-300 shadow-xs transition active:scale-95"
              title="Ir al Tablero Ejecutivo de KPIs"
            >
              <LayoutDashboard size={15} />
              Tablero KPIs
            </Link>
            <button
              onClick={handleExport}
              disabled={exporting || sales.isLoading}
              className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-surface px-3 py-2.5 text-xs sm:text-sm font-semibold text-text hover:bg-muted shadow-sm transition active:scale-95 disabled:opacity-50"
              title="Descargar datos en formato CSV"
            >
              <Download size={14} />
              CSV
            </button>
          </div>
        </div>

        {/* ── 2. Barra de Filtros y Atajos Rápidos de Tiempo ── */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-surface p-4 shadow-card">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider text-text-muted mr-1 flex items-center gap-1.5">
              <Calendar size={15} /> Rango:
            </span>
            {[
              { id: 'today', label: 'Hoy' },
              { id: 'yesterday', label: 'Ayer' },
              { id: '7d', label: 'Últimos 7 días' },
              { id: 'this_month', label: 'Este Mes' },
              { id: 'last_month', label: 'Mes Anterior' },
            ].map(p => (
              <button
                key={p.id}
                onClick={() => applyPreset(p.id as any)}
                className={`rounded-xl px-3 py-1.5 text-xs font-bold transition-all ${
                  activePreset === p.id
                    ? 'bg-primary text-white shadow-sm scale-105'
                    : 'bg-muted text-text-muted hover:bg-primary/10 hover:text-primary'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 text-xs">
            <span className="text-text-muted">Personalizado:</span>
            <input
              type="date"
              value={from}
              max={to}
              onChange={e => {
                setFrom(e.target.value);
                setActivePreset('custom');
              }}
              className="rounded-lg border border-border bg-bg px-2.5 py-1.5 text-xs text-text font-medium focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <span className="text-text-muted">—</span>
            <input
              type="date"
              value={to}
              min={from}
              max={today()}
              onChange={e => {
                setTo(e.target.value);
                setActivePreset('custom');
              }}
              className="rounded-lg border border-border bg-bg px-2.5 py-1.5 text-xs text-text font-medium focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        {/* ── 3. Pestañas de Navegación de Reportes ── */}
        <div className="flex overflow-x-auto gap-2 border-b border-border pb-2 no-scrollbar">
          {[
            { id: 'sales', label: '📊 Ventas & KPIs', desc: 'Facturación, Canales y Métodos' },
            { id: 'menu-engineering', label: '🎯 Ingeniería de Menú (BCG)', desc: 'Rentabilidad vs Popularidad' },
            { id: 'waiters', label: '🏆 Rendimiento de Meseros', desc: 'Ranking y Staff KPIs' },
            { id: 'tips', label: '🪙 Control de Propinas', desc: 'Liquidación y Reparto' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`rounded-2xl px-4 py-2.5 text-xs font-bold transition-all whitespace-nowrap flex flex-col items-start ${
                activeTab === tab.id
                  ? 'bg-primary text-white shadow-md scale-[1.02]'
                  : 'bg-surface border border-border text-text-muted hover:border-primary/40 hover:text-text'
              }`}
            >
              <span className="text-xs font-extrabold">{tab.label}</span>
              <span className={`text-[10px] font-normal ${activeTab === tab.id ? 'text-white/80' : 'text-text-muted'}`}>
                {tab.desc}
              </span>
            </button>
          ))}
        </div>

        {/* ── Renderizado según pestaña activa ── */}
        {activeTab === 'menu-engineering' && <MenuEngineeringTab from={from} to={to} />}
        {activeTab === 'waiters' && <WaitersTab from={from} to={to} />}
        {activeTab === 'tips' && <TipsTab from={from} to={to} />}

        {activeTab === 'sales' && (
          <>
        {/* ── Diagnóstico e Insights Inteligentes del Negocio ── */}
        {insights && (
          <div className="rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/5 via-sky-500/5 to-emerald-500/5 p-4 sm:p-5 shadow-card">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={18} className="text-amber-500" />
              <h2 className="text-sm font-bold text-text uppercase tracking-wide">
                Diagnóstico e Insights Clave del Período
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
              <div className="rounded-xl border border-border/80 bg-surface/80 p-3 backdrop-blur-sm">
                <span className="font-semibold text-text-muted block">🌟 Mejor Día de Venta</span>
                <p className="mt-1 font-bold text-sm text-text">{insights.bestDay}</p>
                <p className="text-emerald-600 dark:text-emerald-400 font-bold mt-0.5">
                  {formatCOP(insights.bestDayRevenue)}
                </p>
              </div>

              <div className="rounded-xl border border-border/80 bg-surface/80 p-3 backdrop-blur-sm">
                <span className="font-semibold text-text-muted block">⚡ Hora Pico de Mayor Venta</span>
                <p className="mt-1 font-bold text-sm text-text">{insights.peakHour}</p>
                <p className="text-primary font-bold mt-0.5">
                  {formatCOP(insights.peakHourRevenue)}
                </p>
              </div>

              <div className="rounded-xl border border-border/80 bg-surface/80 p-3 backdrop-blur-sm">
                <span className="font-semibold text-text-muted block">💳 Medio de Pago Predilecto</span>
                <p className="mt-1 font-bold text-sm text-text truncate">{insights.topPayment}</p>
                <p className="text-text-muted mt-0.5">Mayor preferencia de tus clientes</p>
              </div>

              <div className="rounded-xl border border-border/80 bg-surface/80 p-3 backdrop-blur-sm">
                <span className="font-semibold text-text-muted block">🛵 Canal de Venta Principal</span>
                <p className="mt-1 font-bold text-sm text-text truncate">{insights.topChannel}</p>
                <p className="text-text-muted mt-0.5">Formato con mayor demanda</p>
              </div>
            </div>
          </div>
        )}

        {/* ── 4. Tarjetas Principales de KPIs ── */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <KpiCard
            label="Facturación Total"
            value={formatCOP(sales.data?.totalRevenue ?? 0)}
            sublabel="Ventas netas cobradas"
            growth={growth?.revenue}
            icon={<TrendingUp size={24} />}
            loading={sales.isLoading}
            accentBg="bg-emerald-500/10"
            accentText="text-emerald-600 dark:text-emerald-400"
          />
          <KpiCard
            label="Órdenes Cobradas"
            value={String(sales.data?.totalOrders ?? 0)}
            sublabel="Transacciones completadas"
            growth={growth?.orders}
            icon={<ShoppingBag size={24} />}
            loading={sales.isLoading}
            accentBg="bg-blue-500/10"
            accentText="text-blue-600 dark:text-blue-400"
          />
          <KpiCard
            label="Ticket Promedio"
            value={formatCOP(sales.data?.avgTicket ?? 0)}
            sublabel="Gasto promedio por cliente"
            growth={growth?.avgTicket}
            icon={<Receipt size={24} />}
            loading={sales.isLoading}
            accentBg="bg-purple-500/10"
            accentText="text-purple-600 dark:text-purple-400"
          />
        </div>

        {/* ── 5. Gráfico Principal de Evolución (Interactiva con Tabs) ── */}
        <div className="rounded-2xl border border-border bg-surface p-6 shadow-card">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
            <div>
              <h2 className="text-base font-bold text-text">Evolución de Rendimiento por Día</h2>
              <p className="text-xs text-text-muted">Comportamiento diario a lo largo del período seleccionado</p>
            </div>

            {/* Selector de métrica para el gráfico */}
            <div className="inline-flex rounded-xl border border-border bg-bg p-1 text-xs font-semibold">
              <button
                type="button"
                onClick={() => setChartView('revenue')}
                className={`px-3 py-1.5 rounded-lg transition ${
                  chartView === 'revenue' ? 'bg-primary text-white shadow-sm' : 'text-text-muted hover:text-text'
                }`}
              >
                Facturación ($)
              </button>
              <button
                type="button"
                onClick={() => setChartView('orders')}
                className={`px-3 py-1.5 rounded-lg transition ${
                  chartView === 'orders' ? 'bg-primary text-white shadow-sm' : 'text-text-muted hover:text-text'
                }`}
              >
                Pedidos (#)
              </button>
              <button
                type="button"
                onClick={() => setChartView('avgTicket')}
                className={`px-3 py-1.5 rounded-lg transition ${
                  chartView === 'avgTicket' ? 'bg-primary text-white shadow-sm' : 'text-text-muted hover:text-text'
                }`}
              >
                Ticket Promedio
              </button>
            </div>
          </div>

          {sales.isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="h-9 w-9 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : dailyData.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center text-sm text-text-muted">
              <ShoppingBag size={32} className="opacity-20 mb-2" />
              Sin ventas registradas en el período seleccionado
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              {chartView === 'revenue' ? (
                <AreaChart data={dailyData} margin={{ top: 10, right: 12, bottom: 0, left: 10 }}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#059669" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#059669" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(150, 150, 150, 0.15)" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#888" />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v: number) => '$ ' + (v / 1000).toFixed(0) + 'k'}
                    stroke="#888"
                    width={55}
                  />
                  <Tooltip content={<CustomChartTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="totalRevenue"
                    name="Facturación"
                    stroke="#059669"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#revenueGradient)"
                  />
                </AreaChart>
              ) : chartView === 'orders' ? (
                <BarChart data={dailyData} margin={{ top: 10, right: 12, bottom: 0, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(150, 150, 150, 0.15)" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#888" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#888" width={35} />
                  <Tooltip content={<CustomChartTooltip />} />
                  <Bar dataKey="totalOrders" name="Pedidos" fill="#3B82F6" radius={[6, 6, 0, 0]} />
                </BarChart>
              ) : (
                <LineChart data={dailyData} margin={{ top: 10, right: 12, bottom: 0, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(150, 150, 150, 0.15)" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#888" />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v: number) => '$ ' + (v / 1000).toFixed(0) + 'k'}
                    stroke="#888"
                    width={55}
                  />
                  <Tooltip content={<CustomChartTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="avgTicket"
                    name="Ticket Promedio"
                    stroke="#8B5CF6"
                    strokeWidth={3}
                    dot={{ r: 4, fill: '#8B5CF6' }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              )}
            </ResponsiveContainer>
          )}
        </div>

        {/* ── 6. Gráficas de Dona: Medios de Pago y Canales de Venta ── */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

          {/* Medios de Pago */}
          <div className="rounded-2xl border border-border bg-surface p-6 shadow-card flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CreditCard size={18} className="text-primary" />
                <h2 className="text-base font-bold text-text">Medios de Pago</h2>
              </div>
              <span className="text-xs text-text-muted">Participación %</span>
            </div>

            {paymentMethods.length === 0 ? (
              <div className="flex flex-1 items-center justify-center text-sm text-text-muted py-10">
                Sin datos de pagos en este período
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-center gap-6 flex-1">
                <div className="h-52 w-52 shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={paymentMethods}
                        dataKey="amount"
                        nameKey="method"
                        innerRadius={55}
                        outerRadius={80}
                        paddingAngle={3}
                      >
                        {paymentMethods.map((entry, index) => (
                          <Cell key={`cell-pm-${index}`} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(val: number) => [formatCOP(val), 'Total']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="flex-1 space-y-2.5 w-full">
                  {paymentMethods.map((pm, index) => (
                    <div key={pm.method} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="h-3 w-3 shrink-0 rounded-full"
                          style={{ backgroundColor: DONUT_COLORS[index % DONUT_COLORS.length] }}
                        />
                        <span className="font-semibold text-text truncate">{pm.method}</span>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="font-bold text-text">{formatCOP(pm.amount)}</span>
                        <span className="ml-1.5 text-text-muted">({pm.percentage}%)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Canales de Consumo (Salón vs Para Llevar vs Domicilio) */}
          <div className="rounded-2xl border border-border bg-surface p-6 shadow-card flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Store size={18} className="text-primary" />
                <h2 className="text-base font-bold text-text">Canales de Consumo</h2>
              </div>
              <span className="text-xs text-text-muted">Venta por Destino</span>
            </div>

            {fulfillmentChannels.length === 0 ? (
              <div className="flex flex-1 items-center justify-center text-sm text-text-muted py-10">
                Sin pedidos en este período
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-center gap-6 flex-1">
                <div className="h-52 w-52 shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={fulfillmentChannels}
                        dataKey="amount"
                        nameKey="channel"
                        innerRadius={55}
                        outerRadius={80}
                        paddingAngle={3}
                      >
                        {fulfillmentChannels.map((entry, index) => (
                          <Cell key={`cell-fc-${index}`} fill={DONUT_COLORS[(index + 3) % DONUT_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(val: number) => [formatCOP(val), 'Total']} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="flex-1 space-y-2.5 w-full">
                  {fulfillmentChannels.map((fc, index) => (
                    <div key={fc.channel} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="h-3 w-3 shrink-0 rounded-full"
                          style={{ backgroundColor: DONUT_COLORS[(index + 3) % DONUT_COLORS.length] }}
                        />
                        <span className="font-semibold text-text truncate">{fc.channel}</span>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="font-bold text-text">{formatCOP(fc.amount)}</span>
                        <span className="ml-1.5 text-text-muted">({fc.percentage}%)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── 7. Ventas por Categoría de Producto ── */}
        {categories.length > 0 && (
          <div className="rounded-2xl border border-border bg-surface p-6 shadow-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Layers size={18} className="text-primary" />
                <h2 className="text-base font-bold text-text">Ventas por Categoría</h2>
              </div>
              <span className="text-xs text-text-muted">Familias de productos</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {categories.map((cat, idx) => (
                <div key={cat.name} className="rounded-xl border border-border bg-bg p-3.5 space-y-2">
                  <div className="flex justify-between items-center text-xs">
                    <span className="font-bold text-text">{cat.name}</span>
                    <span className="font-black text-primary">{formatCOP(cat.revenue)}</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${Math.min(100, Math.max(5, cat.percentage))}%` }}
                    />
                  </div>
                  <div className="flex justify-between items-center text-[11px] text-text-muted">
                    <span>{cat.qty} unidades vendidas</span>
                    <span>{cat.percentage}% de la facturación</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── 8. Horas Pico y Distribución Diaria ── */}
        <div className="rounded-2xl border border-border bg-surface p-6 shadow-card">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <div className="flex items-center gap-2">
                <Clock size={18} className="text-primary" />
                <h2 className="text-base font-bold text-text">Horas Pico de Facturación</h2>
              </div>
              <p className="text-xs text-text-muted">Visualiza el flujo de clientes y ventas a lo largo de cada hora del día</p>
            </div>

            <div className="flex items-center gap-2 text-xs">
              <span className="text-text-muted">Día a inspeccionar:</span>
              <input
                type="date"
                value={hourlyDate}
                max={today()}
                onChange={e => setHourlyDate(e.target.value)}
                className="rounded-lg border border-border bg-bg px-2.5 py-1.5 text-xs text-text font-medium focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="button"
                onClick={() => setHourlyDate(today())}
                className="px-2.5 py-1.5 rounded-lg bg-muted text-xs font-semibold hover:bg-primary/10 hover:text-primary transition"
              >
                Hoy
              </button>
            </div>
          </div>

          {hourly.isLoading ? (
            <div className="flex h-56 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={hourlyData} margin={{ top: 10, right: 10, bottom: 0, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(150, 150, 150, 0.15)" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#888" interval={1} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v: number) => '$ ' + (v / 1000).toFixed(0) + 'k'}
                  stroke="#888"
                  width={50}
                />
                <Tooltip content={<CustomChartTooltip />} />
                <Bar dataKey="totalRevenue" name="Ventas" radius={[5, 5, 0, 0]}>
                  {hourlyData.map((entry, index) => (
                    <Cell
                      key={`hourly-cell-${index}`}
                      fill={entry.isPeak ? '#F59E0B' : '#059669'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}

          <div className="mt-3 flex items-center justify-end gap-4 text-xs text-text-muted">
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-600" />
              Horas Regulares
            </span>
            <span className="flex items-center gap-1.5 font-bold text-amber-600">
              <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
              ★ Hora Pico del Día
            </span>
          </div>
        </div>

        {/* ── 9. Rankings: Top Productos y Sabores más Vendidos ── */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

          {/* Top Productos */}
          <div className="rounded-2xl border border-border bg-surface p-6 shadow-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Award size={18} className="text-amber-500" />
                <h2 className="text-base font-bold text-text">Top Productos</h2>
              </div>
              <span className="text-xs text-text-muted">Por volumen e ingresos</span>
            </div>

            {topProducts.isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-12 animate-pulse rounded-xl bg-muted" />
                ))}
              </div>
            ) : (topProducts.data?.products ?? []).length === 0 ? (
              <p className="text-sm text-text-muted py-8 text-center">Sin ventas en este período</p>
            ) : (
              <div className="space-y-3">
                {(topProducts.data?.products ?? []).map((p, idx) => {
                  const maxRevenue = topProducts.data!.products[0]?.totalRevenue ?? 1;
                  const pct = Math.round((p.totalRevenue / maxRevenue) * 100);
                  const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}`;

                  return (
                    <div key={p.id} className="rounded-xl border border-border bg-bg p-3 space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-black w-6 text-center">{medal}</span>
                          <span className="font-bold text-text truncate">{p.name}</span>
                        </div>
                        <span className="font-black text-primary">{formatCOP(p.totalRevenue)}</span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="flex justify-between items-center text-[11px] text-text-muted pl-8">
                        <span>{p.totalQty} unidades</span>
                        {p.share !== undefined && <span>{p.share}% del total facturado</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Top Sabores de Helado */}
          <div className="rounded-2xl border border-border bg-surface p-6 shadow-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <IceCream size={18} className="text-rose-500" />
                <h2 className="text-base font-bold text-text">Sabores Más Populares</h2>
              </div>
              <span className="text-xs text-text-muted">Porciones servidas</span>
            </div>

            {topFlavors.isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-12 animate-pulse rounded-xl bg-muted" />
                ))}
              </div>
            ) : (topFlavors.data?.flavors ?? []).length === 0 ? (
              <p className="text-sm text-text-muted py-8 text-center">Sin consumo de sabores en este período</p>
            ) : (
              <div className="space-y-3">
                {(topFlavors.data?.flavors ?? []).map((f, idx) => {
                  const maxQty = topFlavors.data!.flavors[0]?.totalQty ?? 1;
                  const pct = Math.round((f.totalQty / maxQty) * 100);
                  const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}`;

                  return (
                    <div key={f.id} className="rounded-xl border border-border bg-bg p-3 space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-black w-6 text-center">{medal}</span>
                          <span className="font-bold text-text truncate">{f.name}</span>
                        </div>
                        <span className="font-bold text-rose-600 dark:text-rose-400">
                          {f.totalQty} bolas/porciones
                        </span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full bg-rose-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── 10. Tiempos Operativos de Preparación por Área ── */}
        <div className="rounded-2xl border border-border bg-surface p-6 shadow-card">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap size={18} className="text-amber-500" />
              <h2 className="text-base font-bold text-text">Eficiencia y Tiempos de Preparación</h2>
            </div>
            <span className="text-xs text-text-muted">KDS / Cocina</span>
          </div>

          {prepTime.isLoading ? (
            <div className="space-y-3">
              {[1, 2].map(i => (
                <div key={i} className="h-16 animate-pulse rounded-xl bg-muted" />
              ))}
            </div>
          ) : (prepTime.data?.areas ?? []).length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-xs text-text-muted">
              <Clock size={28} className="opacity-30" />
              <p>Sin tiempos registrados en este período.</p>
              <p>Los tiempos se auditan cuando los pedidos cambian de estado en cocina.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {(prepTime.data?.areas ?? []).map(area => (
                <div
                  key={area.area}
                  className="rounded-xl border border-border bg-bg p-4 space-y-2.5"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-text uppercase tracking-wide">{area.areaLabel}</span>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary">
                      {area.totalOrders} órdenes
                    </span>
                  </div>

                  <div className="text-center py-2">
                    <p className="text-3xl font-black text-text">
                      {area.avgMinutes}
                      <span className="text-sm font-normal text-text-muted ml-1">min</span>
                    </p>
                    <p className="text-[11px] text-text-muted mt-0.5">Tiempo promedio de despacho</p>
                  </div>

                  <div className="flex justify-between text-xs border-t border-border pt-2 text-text-muted">
                    <span>Mínimo: <strong className="text-emerald-600">{area.minMinutes}m</strong></span>
                    <span>Máximo: <strong className="text-rose-500">{area.maxMinutes}m</strong></span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
          </>
        )}

      </div>
    </div>
  );
}
